import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import StarRating from "./StarRating";
import { api } from "../../services/api";

const TestimonialManager = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Form data state
  const [formData, setFormData] = useState({
    name: "",
    role: "",
    company: "",
    content: "",
    rating: 5,
    avatar: "",
    featured: false,
    verified: false,
    displayOrder: 1,
  });

  // Fetch testimonials
  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const response = await api.testimonials.getAll();

      if (response.success) {
        setTestimonials(response.data);
        setError(null);
      } else {
        setError(response.message || "Failed to fetch testimonials");
        toast.error("Failed to fetch testimonials");
      }
    } catch (err) {
      setError("Failed to fetch testimonials: " + err.message);
      toast.error("Failed to fetch testimonials");
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchTestimonials();
  }, []);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Handle rating change
  const handleRatingChange = (rating) => {
    setFormData({
      ...formData,
      rating,
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      let response;

      if (editingId) {
        // Update existing testimonial
        response = await api.testimonials.update(editingId, formData);
      } else {
        // Create new testimonial
        response = await api.testimonials.create(formData);
      }

      if (response.success) {
        toast.success(
          editingId
            ? "Testimonial updated successfully"
            : "Testimonial created successfully"
        );
        resetForm();
        fetchTestimonials();
      } else {
        setError(response.message || "Failed to save testimonial");
        toast.error("Failed to save testimonial");
      }
    } catch (err) {
      setError("Failed to save testimonial: " + err.message);
      toast.error("Failed to save testimonial");
    } finally {
      setLoading(false);
    }
  };

  // Reset form data and state
  const resetForm = () => {
    setFormData({
      name: "",
      role: "",
      company: "",
      content: "",
      rating: 5,
      avatar: "",
      featured: false,
      verified: false,
      displayOrder: 1,
    });
    setEditingId(null);
    setShowForm(false);
  };

  // Edit testimonial
  const handleEdit = (testimonial) => {
    setFormData({
      name: testimonial.name,
      role: testimonial.role || "",
      company: testimonial.company || "",
      content: testimonial.content,
      rating: testimonial.rating,
      avatar: testimonial.avatar || "",
      featured: testimonial.featured,
      verified: testimonial.verified,
      displayOrder: testimonial.displayOrder || 1,
    });
    setEditingId(testimonial.id);
    setShowForm(true);
  };

  // Delete testimonial
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this testimonial?")) {
      try {
        setLoading(true);
        const response = await api.testimonials.delete(id);

        if (response.success) {
          toast.success("Testimonial deleted successfully");
          fetchTestimonials();
        } else {
          setError(response.message || "Failed to delete testimonial");
          toast.error("Failed to delete testimonial");
        }
      } catch (err) {
        setError("Failed to delete testimonial: " + err.message);
        toast.error("Failed to delete testimonial");
      } finally {
        setLoading(false);
      }
    }
  };

  // Toggle featured status
  const handleToggleFeatured = async (id) => {
    try {
      setLoading(true);
      const response = await api.testimonials.toggleFeatured(id);

      if (response.success) {
        fetchTestimonials();
      } else {
        setError(response.message || "Failed to update featured status");
        toast.error("Failed to update featured status");
      }
    } catch (err) {
      setError("Failed to update featured status: " + err.message);
      toast.error("Failed to update featured status");
    } finally {
      setLoading(false);
    }
  };

  // Toggle verified status
  const handleToggleVerified = async (id) => {
    try {
      setLoading(true);
      const response = await api.testimonials.toggleVerified(id);

      if (response.success) {
        fetchTestimonials();
      } else {
        setError(response.message || "Failed to update verified status");
        toast.error("Failed to update verified status");
      }
    } catch (err) {
      setError("Failed to update verified status: " + err.message);
      toast.error("Failed to update verified status");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="col-lg-12">
      <div className="card h-100">
        <div className="card-header bg-white border-bottom-0 pb-0 pt-4">
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Testimonials Management</h4>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => {
                resetForm();
                setShowForm(!showForm);
              }}
            >
              {showForm ? "Cancel" : "Add Testimonial"}
            </button>
          </div>
        </div>
        <div className="card-body pt-3">
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {/* Testimonial Form */}
          {showForm && (
            <div className="mb-4 p-3 border rounded">
              <h5>{editingId ? "Edit Testimonial" : "Add New Testimonial"}</h5>
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Role</label>
                    <input
                      type="text"
                      className="form-control"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Company</label>
                    <input
                      type="text"
                      className="form-control"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Avatar URL</label>
                    <input
                      type="text"
                      className="form-control"
                      name="avatar"
                      value={formData.avatar}
                      onChange={handleChange}
                      placeholder="https://example.com/avatar.jpg"
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Testimonial Content *</label>
                  <textarea
                    className="form-control"
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    rows="3"
                    required
                  ></textarea>
                </div>
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Rating *</label>
                    <div className="d-flex align-items-center">
                      <StarRating
                        rating={formData.rating}
                        onRatingChange={handleRatingChange}
                        editable={true}
                      />
                      <span className="ms-2">{formData.rating}/5</span>
                    </div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Display Order</label>
                    <input
                      type="number"
                      className="form-control"
                      name="displayOrder"
                      value={formData.displayOrder}
                      onChange={handleChange}
                      min="1"
                    />
                  </div>
                  <div className="col-md-4 mb-3 d-flex">
                    <div className="form-check me-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        name="featured"
                        checked={formData.featured}
                        onChange={handleChange}
                        id="featuredCheck"
                      />
                      <label
                        className="form-check-label"
                        htmlFor="featuredCheck"
                      >
                        Featured
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        name="verified"
                        checked={formData.verified}
                        onChange={handleChange}
                        id="verifiedCheck"
                      />
                      <label
                        className="form-check-label"
                        htmlFor="verifiedCheck"
                      >
                        Verified
                      </label>
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? "Saving..." : "Save Testimonial"}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Testimonials List */}
          {loading && !showForm ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-striped">
                <thead className="table-light">
                  <tr>
                    <th>Name</th>
                    <th>Role/Company</th>
                    <th>Content</th>
                    <th>Rating</th>
                    <th>Status</th>
                    <th>Order</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {testimonials.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        No testimonials found. Add your first one!
                      </td>
                    </tr>
                  ) : (
                    testimonials.map((testimonial) => (
                      <tr key={testimonial.id}>
                        <td>
                          <div className="d-flex align-items-center">
                            {testimonial.avatar ? (
                              <img
                                src={testimonial.avatar}
                                alt={testimonial.name}
                                className="rounded-circle me-2"
                                width="40"
                                height="40"
                                onError={(e) => {
                                  e.target.src =
                                    "https://ui-avatars.com/api/?name=" +
                                    encodeURIComponent(testimonial.name);
                                }}
                              />
                            ) : (
                              <div
                                className="rounded-circle me-2 d-flex align-items-center justify-content-center bg-light"
                                style={{ width: "40px", height: "40px" }}
                              >
                                {testimonial.name.charAt(0)}
                              </div>
                            )}
                            <span>{testimonial.name}</span>
                          </div>
                        </td>
                        <td>
                          {testimonial.role && <div>{testimonial.role}</div>}
                          {testimonial.company && (
                            <small className="text-muted">
                              {testimonial.company}
                            </small>
                          )}
                        </td>
                        <td>
                          <div
                            className="text-truncate"
                            style={{ maxWidth: "200px" }}
                          >
                            {testimonial.content}
                          </div>
                        </td>
                        <td>
                          <StarRating rating={testimonial.rating} />
                        </td>
                        <td>
                          <div className="d-flex flex-column">
                            <div className="badge mb-1 rounded-pill bg-primary-subtle text-primary">
                              {testimonial.featured
                                ? "Featured"
                                : "Not Featured"}
                            </div>
                            <div className="badge rounded-pill bg-success-subtle text-success">
                              {testimonial.verified
                                ? "Verified"
                                : "Not Verified"}
                            </div>
                          </div>
                        </td>
                        <td>{testimonial.displayOrder || "-"}</td>
                        <td>
                          <div className="dropdown">
                            <button
                              className="btn btn-sm btn-icon"
                              type="button"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            >
                              <i className="ti ti-dots-vertical"></i>
                            </button>
                            <ul className="dropdown-menu">
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() => handleEdit(testimonial)}
                                >
                                  <i className="ti ti-edit me-1"></i> Edit
                                </button>
                              </li>
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() =>
                                    handleToggleFeatured(testimonial.id)
                                  }
                                >
                                  <i
                                    className={`ti ti-star${
                                      testimonial.featured ? "-off" : ""
                                    } me-1`}
                                  ></i>
                                  {testimonial.featured
                                    ? "Unfeature"
                                    : "Feature"}
                                </button>
                              </li>
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() =>
                                    handleToggleVerified(testimonial.id)
                                  }
                                >
                                  <i
                                    className={`ti ti-check${
                                      testimonial.verified ? "-off" : ""
                                    } me-1`}
                                  ></i>
                                  {testimonial.verified ? "Unverify" : "Verify"}
                                </button>
                              </li>
                              <li>
                                <hr className="dropdown-divider" />
                              </li>
                              <li>
                                <button
                                  className="dropdown-item text-danger"
                                  onClick={() => handleDelete(testimonial.id)}
                                >
                                  <i className="ti ti-trash me-1"></i> Delete
                                </button>
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestimonialManager;
